import { useId } from "react";

import { Select } from "../select/select.tsx";
import styles from "./time-field.module.css";

type TimeFieldProps = {
  label: string;
  value: string;
  onChange?: (value: string) => void;
  readOnly?: boolean;
  required?: boolean;
};

const HOURS = Array.from({ length: 12 }, (_, index) => ({
  value: String(index + 1),
  label: String(index + 1).padStart(2, "0"),
}));

// Sixty valid minute values are 00–59; 60 rolls over to the next hour.
const MINUTES = Array.from({ length: 60 }, (_, index) => {
  const value = String(index).padStart(2, "0");
  return { value, label: value };
});

const MERIDIEMS = [
  { value: "AM", label: "AM" },
  { value: "PM", label: "PM" },
];

function timeParts(value: string) {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value);
  if (!match) return { hour: "", minute: "", meridiem: "" };
  const hour24 = Number(match[1]);
  return {
    hour: String(hour24 % 12 || 12),
    minute: match[2],
    meridiem: hour24 < 12 ? "AM" : "PM",
  };
}

/** Reusable 12-hour time selector backed by a normalized HH:mm value. */
export function TimeField({ label, value, onChange, readOnly = false, required = false }: TimeFieldProps) {
  const id = useId().replace(/:/g, "");
  const { hour, minute, meridiem } = timeParts(value);

  function update(nextHour: string, nextMinute: string, nextMeridiem: string) {
    if (!nextHour || !nextMinute || !nextMeridiem) return;
    const hour24 = Number(nextHour) % 12 + (nextMeridiem === "PM" ? 12 : 0);
    onChange?.(`${String(hour24).padStart(2, "0")}:${nextMinute}`);
  }

  if (readOnly) {
    return <div className={styles.field}>
      <span className={`${styles.label} sub-heading-md`}>{label}</span>
      <div className={styles.readOnlyValue} aria-label={label}>
        {value ? `${String(hour).padStart(2, "0")}:${minute} ${meridiem}` : "Calculated automatically"}
      </div>
    </div>;
  }

  return <fieldset className={styles.field}>
    <legend className={`${styles.label} sub-heading-md`}>{label}{required && <span className={styles.required}> *</span>}</legend>
    <div className={styles.selects}>
      <Select id={`${id}-hour`} label="Hour" value={hour} options={HOURS} placeholder="Hour" onChange={(next) => update(next, minute || "00", meridiem || "AM")} />
      <Select id={`${id}-minute`} label="Minute" value={minute} options={MINUTES} placeholder="Minute" onChange={(next) => update(hour || "12", next, meridiem || "AM")} />
      <Select id={`${id}-meridiem`} label="AM / PM" value={meridiem} options={MERIDIEMS} placeholder="AM/PM" onChange={(next) => update(hour || "12", minute || "00", next)} />
    </div>
  </fieldset>;
}
